import { createBrowserRouter, redirect } from 'react-router-dom'
import type { RouteObject } from 'react-router-dom'
import { appMode } from '~/config'
import { App } from '../App'
import { FeatureAvailabilityRoute } from './FeatureAvailabilityRoute'
import { OnlyOnlineRoute } from './OnlyOnlineRoute'
import { PlaceListPageRoute } from './PlaceListPageRoute'
import { PlaceNodeSelectedRoute } from './PlaceNodeSelectedRoute'
import { ProtectedRoute } from './ProtectedRoute'
import { TemplateHideSettingRoute } from './TemplateHideSettingRoute'
import { TemplateListPageRoute } from './TemplateListPageRoute'
import { routes } from './paths'

// キオスク端末（kiosk）にはログインの概念が無いため /login ページを提供しない。
// / へ戻せば ProtectedRoute が Device 認証（fetchSession → GET /me）を自動で行い、
// 失敗時はその場に全画面エラー（KioskAuthError）を被せる。
// PWA の start_url や外部リンクが /login のまま残っている端末のために、ルート自体は消さず
// loader でリダイレクトする（消すと NotFound が起動画面になる）。
// loader は要素の描画前に走るため、旧ログイン画面（クッションページ）は一瞬も描画されない。
// 関数にしているのは、テストで appMode を切り替えてから組み立て直せるようにするため。
export const createLoginRoute = (): RouteObject =>
  appMode.isKiosk
    ? {
        path: routes.login.path,
        loader: () => redirect(routes.home.path),
      }
    : {
        path: routes.login.path,
        lazy: async () => {
          const { RumpLogin } = await import('~/pages/RumpLogin/RumpLogin')
          return { element: <RumpLogin /> }
        },
      }

export const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      createLoginRoute(),
      {
        path: routes.places.path,
        lazy: async () => {
          const { PlacesList } = await import('~/pages/PlacesList/PlacesList')
          return {
            element: (
              <ProtectedRoute>
                <PlaceListPageRoute>
                  <PlacesList />
                </PlaceListPageRoute>
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: routes.templates.path,
        lazy: async () => {
          const { TemplatesList } =
            await import('~/pages/TemplatesList/TemplatesList')
          return {
            element: (
              <ProtectedRoute>
                <TemplateListPageRoute>
                  <TemplatesList />
                </TemplateListPageRoute>
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: routes.home.path,
        lazy: async () => {
          const { Home } = await import('~/pages/Home/Home')
          return {
            element: (
              <ProtectedRoute>
                <PlaceNodeSelectedRoute>
                  <Home />
                </PlaceNodeSelectedRoute>
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: routes.reportsNew.path,
        lazy: async () => {
          const { ReportsNew } = await import('~/pages/ReportsNew/ReportsNew')
          return {
            element: (
              <ProtectedRoute>
                <ReportsNew />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: routes.ReportsNewFromProductMaster.path,
        lazy: async () => {
          const { ReportsNewFromProductMaster } =
            await import('~/pages/ReportsNewFromProductMaster/ReportsNewFromProductMaster')
          return {
            element: (
              <ProtectedRoute>
                <OnlyOnlineRoute>
                  <PlaceNodeSelectedRoute>
                    <ReportsNewFromProductMaster />
                  </PlaceNodeSelectedRoute>
                </OnlyOnlineRoute>
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: routes.reportsNewFromTaskSchedule.path,
        lazy: async () => {
          const { ReportsNewFromTaskSchedule } =
            await import('~/pages/ReportsNewFromTaskSchedule/ReportsNewFromTaskSchedule')
          return {
            element: (
              <ProtectedRoute>
                <OnlyOnlineRoute>
                  <FeatureAvailabilityRoute feature="taskSchedule">
                    <ReportsNewFromTaskSchedule />
                  </FeatureAvailabilityRoute>
                </OnlyOnlineRoute>
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: routes.reportsNewFromSchedule.path,
        lazy: async () => {
          const { ReportsNewFromSchedule } =
            await import('~/pages/ReportsNewFromSchedule/ReportsNewFromSchedule')
          return {
            element: (
              <ProtectedRoute>
                <ReportsNewFromSchedule />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: routes.reportsCompleted.path,
        lazy: async () => {
          const { ReportsCompleted } =
            await import('~/pages/ReportsCompleted/ReportsCompleted')
          return {
            element: (
              <ProtectedRoute>
                <ReportsCompleted />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: routes.reportsCompletedForceUpdate.path,
        lazy: async () => {
          const { ReportsCompletedForceUpdate } =
            await import('~/pages/ReportsCompletedForceUpdate/ReportsCompletedForceUpdate')
          return {
            element: (
              <ProtectedRoute>
                <OnlyOnlineRoute>
                  <ReportsCompletedForceUpdate />
                </OnlyOnlineRoute>
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: routes.reports.path,
        lazy: async () => {
          const { ReportsList } =
            await import('~/pages/ReportsList/ReportsList')
          return {
            element: (
              <ProtectedRoute>
                <OnlyOnlineRoute>
                  <ReportsList />
                </OnlyOnlineRoute>
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: routes.reportsDetail.path,
        lazy: async () => {
          const { ReportsDetail } =
            await import('~/pages/ReportDetail/ReportDetail')
          return {
            element: (
              <ProtectedRoute>
                <OnlyOnlineRoute>
                  <ReportsDetail />
                </OnlyOnlineRoute>
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: routes.reportsEdit.path,
        lazy: async () => {
          const { ReportsEdit } =
            await import('~/pages/ReportsEdit/ReportsEdit')
          return {
            element: (
              <ProtectedRoute>
                <OnlyOnlineRoute>
                  <ReportsEdit />
                </OnlyOnlineRoute>
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: routes.expiredCalendar.path,
        lazy: async () => {
          const { ExpiredCalendar } =
            await import('~/pages/ExpiredCalendar/ExpiredCalendar')
          return {
            element: (
              <ProtectedRoute>
                <OnlyOnlineRoute>
                  <ExpiredCalendar />
                </OnlyOnlineRoute>
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: routes.reportsOffline.path,
        lazy: async () => {
          const { ReportsOffline } =
            await import('~/pages/ReportsOffline/ReportsOffline')
          return {
            element: (
              <ProtectedRoute>
                <ReportsOffline />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: routes.reportsOfflineDetail.path,
        lazy: async () => {
          const { ReportsOfflineDetail } =
            await import('~/pages/ReportsOfflineDetail/ReportsOfflineDetail.tsx')
          return {
            element: (
              <ProtectedRoute>
                <ReportsOfflineDetail />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: routes.reportsOfflineEdit.path,
        lazy: async () => {
          const { ReportsOfflineEdit } =
            await import('~/pages/ReportsOfflineEdit/ReportsOfflineEdit')
          return {
            element: (
              <ProtectedRoute>
                <ReportsOfflineEdit />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: routes.templateHideSettingList.path,
        lazy: async () => {
          const { TemplateHideSettingList } =
            await import('~/pages/TemplateHideSettingList/TemplateHideSettingList')
          return {
            element: (
              <TemplateHideSettingRoute>
                <TemplateHideSettingList />
              </TemplateHideSettingRoute>
            ),
          }
        },
      },
      {
        path: routes.templateHideSettingEdit.path,
        lazy: async () => {
          const { TemplateHideSetting } =
            await import('~/pages/TemplateHideSetting/TemplateHideSetting')
          return {
            element: (
              <TemplateHideSettingRoute>
                <TemplateHideSetting />
              </TemplateHideSettingRoute>
            ),
          }
        },
      },
      {
        path: routes.productsTemplate.path,
        lazy: async () => {
          const { ProductsListForTemplate } =
            await import('~/pages/ProductsListForTemplate/ProductsListForTemplate')
          return {
            element: (
              <ProtectedRoute>
                <OnlyOnlineRoute>
                  <PlaceNodeSelectedRoute>
                    <ProductsListForTemplate />
                  </PlaceNodeSelectedRoute>
                </OnlyOnlineRoute>
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: routes.productMasterTemplates.path,
        lazy: async () => {
          const { ProductMasterTemplatesList } =
            await import('~/pages/ProductMasterTemplatesList/ProductMasterTemplatesList')
          return {
            element: (
              <ProtectedRoute>
                <OnlyOnlineRoute>
                  <PlaceNodeSelectedRoute>
                    <ProductMasterTemplatesList />
                  </PlaceNodeSelectedRoute>
                </OnlyOnlineRoute>
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: routes.tasks.path,
        lazy: async () => {
          const { Tasks } = await import('~/pages/Tasks/Tasks')
          return {
            element: (
              <ProtectedRoute>
                <OnlyOnlineRoute>
                  <FeatureAvailabilityRoute feature="taskSchedule">
                    <Tasks />
                  </FeatureAvailabilityRoute>
                </OnlyOnlineRoute>
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: routes.language.path,
        lazy: async () => {
          const { LanguageSelection } =
            await import('~/pages/LanguageSelection/LanguageSelection')
          return {
            element: (
              <ProtectedRoute>
                <OnlyOnlineRoute>
                  <FeatureAvailabilityRoute feature="multiLanguageSupport">
                    <LanguageSelection />
                  </FeatureAvailabilityRoute>
                </OnlyOnlineRoute>
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: routes.lobsterBenchmark.path,
        lazy: async () => {
          const { LobsterBenchmark } =
            await import('~/pages/LobsterBenchmark/LobsterBenchmark')
          return {
            element: (
              <ProtectedRoute>
                <LobsterBenchmark />
              </ProtectedRoute>
            ),
          }
        },
      },
      {
        path: routes.notFound.path,
        lazy: async () => {
          const { NotFound } = await import('~/pages/NotFound/NotFound')
          return {
            element: <NotFound />,
          }
        },
      },
      // どのルートにも当てはまらない場合は404ページを表示
      {
        path: '*',
        lazy: async () => {
          const { NotFound } = await import('~/pages/NotFound/NotFound')
          return {
            element: <NotFound />,
          }
        },
      },
    ],
  },
])
